import type { ViewjobQueryResult } from '../__generated__/viewjob.gqlgenerated';

import { DatadogLogger } from '../../../shared/logging/DatadogLogger';
import { isJobSponsored } from '../../../shared/util/urlUtils';
import { useApplyLinkQuery } from '../__generated__/applyLink.gqlgenerated';

import { useEffect, useMemo } from 'react';

type UseApplyLinkProps = {
    jobKey: string | null | undefined;
    viewJobData: ViewjobQueryResult['data'];
    viewJobQueryCompleted: boolean;
    url: string | null;
    jobClickUrl: string | null | undefined;
};

// WENG-2423: searchless `from` values whose spn override behavior we want to
// observe in PROD. Mirrors the saved-job + app-share lists in
// `getJobDataInput.ts`. Used only for logging.
const SEARCHLESS_FROM_VALUES_FOR_LOGGING = [
    'app-tracker-saved-appcard',
    'app-tracker-saved-applybtn',
    'savedjoblist',
    'myjobs-saved',
    'myjobs-saved-apply',
    'appshareios',
    'appsharedroid',
    'appshareandroid',
];

function safeGetFromParam(url: string | null): string {
    try {
        return new URL(url ?? '').searchParams.get('from') ?? '';
    } catch {
        return '';
    }
}

/**
 * Determines the correct sponsored (spn) value by overriding with jobClick.url data.
 * Aligns with ViewJobClickDataFactory#overrideSponsoredInfoFromOneGraphJob:
 * the Java override only fires when the `ad` query param contains a valid AdBlob
 * (i.e. sponsored /pagead/clk URLs). For organic URLs (/rc/clk), the `ad` param
 * is absent so the blob parse returns null and the original value is preserved.
 */
function overrideSponsoredFromJobClickUrl(
    serverIsSponsored: boolean,
    jobClickUrl: string | null | undefined,
): boolean {
    return isJobSponsored(jobClickUrl) || serverIsSponsored;
}

export function getApplyLinkSource(url: string | null, isApplyPlusRsvpEnabled: boolean): string {
    const indeedApplySource = url ? new URL(url).searchParams.get('indapplysrc') : null;

    // TODO: Support isDesktop for React Native Web
    const isDesktop = false;

    let source: string;
    if (indeedApplySource) {
        source = indeedApplySource;
    } else if (!isDesktop) {
        source = 'idm';
    } else {
        source = 'idd-vj';
    }

    if (isApplyPlusRsvpEnabled) {
        return source + '-ihp-apply-rsvp';
    }
    return source;
}

/**
 * Hook to fetch the Indeed Apply link URL asynchronously.
 * This query depends on data from the viewjob query (mobvjtk, continueUrl, clientMeta).
 * It runs after the viewjob query completes and does not block the main content rendering.
 */
export const useApplyLink = ({
    jobKey,
    viewJobData,
    viewJobQueryCompleted,
    url,
    jobClickUrl,
}: UseApplyLinkProps) => {
    const applyMethod = viewJobData?.viewjob?.details?.applyMethod;
    const indeedApplyMethod = applyMethod?.__typename === 'ViewJobIndeedApply' ? applyMethod : null;

    const source = getApplyLinkSource(url, indeedApplyMethod?.isApplyPlusRsvpEnabled === true);
    const serverIsSponsored = viewJobData?.viewjob?.details?.isSponsored ?? false;
    const spn = useMemo(
        () => overrideSponsoredFromJobClickUrl(serverIsSponsored, jobClickUrl),
        [serverIsSponsored, jobClickUrl],
    );

    // WENG-2423: temporary instrumentation. Logs the resolved `spn` plus its
    // two inputs (viewjob subgraph isSponsored vs jobClickUrl-derived) for
    // searchless `from` values, so we can determine in PROD whether the
    // override is producing spn=true and which input is driving the decision.
    useEffect(() => {
        if (!viewJobQueryCompleted || !jobKey || !indeedApplyMethod) {
            return;
        }
        const from = safeGetFromParam(url);
        if (!SEARCHLESS_FROM_VALUES_FOR_LOGGING.includes(from)) {
            return;
        }
        void DatadogLogger.info('[WENG-2423] useApplyLink spn', {
            from,
            spn,
            serverIsSponsored,
            jobClickUrlSponsored: isJobSponsored(jobClickUrl),
            hasJobClickUrl: jobClickUrl != null && jobClickUrl !== '',
            jobKey,
        });
    }, [
        viewJobQueryCompleted,
        jobKey,
        indeedApplyMethod,
        spn,
        serverIsSponsored,
        jobClickUrl,
        url,
    ]);

    const { data, loading, error, refetch } = useApplyLinkQuery({
        variables: {
            input: jobKey ?? '',
            mobvjtk: viewJobData?.viewjob?.details?.mobvjtk ?? '',
            continueUrl: indeedApplyMethod?.continueUrl ?? '',
            spn,
            clientMeta: indeedApplyMethod?.clientMeta ?? '',
            source,
        },
        fetchPolicy: 'no-cache',
        skip: !viewJobQueryCompleted || !jobKey || !indeedApplyMethod,
    });

    const applyLinkUrl = data?.jobData?.results?.[0]?.job?.indeedApply?.applyLink?.url ?? null;

    return {
        applyLinkUrl,
        applyLinkLoading: loading,
        applyLinkError: error,
        refetchApplyLink: refetch,
    };
};
